import type {MediaPlayer, PlayerError, PlayerEventType, Quality} from 'amazon-ivs-player';

import {logger} from '../../../../utils/Logger';
import {ErrorService} from '../playback/ErrorService';
import {PlaybackService} from '../playback/PlaybackService';
import {VideoStartupService} from '../playback/VideoStartupService';

/**
 * Map of IVS Player events that we listen to, each should be type of {@link PlayerEventType}.
 *
 * Why: This way, we don't need to import string enum {@link PlayerEventType} from 'amazon-ivs-player' directly,
 * aka. compile/include it (as value) in our distribution code
 */
export const IVS_PLAYER_EVENT = {
  DURATION_CHANGED: 'PlayerDurationChanged' as PlayerEventType.DURATION_CHANGED,
  ERROR: 'PlayerError' as PlayerEventType.ERROR,
  INITIALIZED: 'PlayerInitialized' as PlayerEventType.INITIALIZED,
  QUALITY_CHANGED: 'PlayerQualityChanged' as PlayerEventType.QUALITY_CHANGED,
  REBUFFERING: 'PlayerRebuffering' as PlayerEventType.REBUFFERING,
  SEEK_COMPLETED: 'PlayerSeekCompleted' as PlayerEventType.SEEK_COMPLETED,
  TIME_UPDATE: 'PlayerTimeUpdate' as PlayerEventType.TIME_UPDATE,
  PLAYBACK_RATE_CHANGED: 'PlayerPlaybackRateChanged' as PlayerEventType.PLAYBACK_RATE_CHANGED,
  PLAYBACK_BLOCKED: 'PlayerPlaybackBlocked' as PlayerEventType.PLAYBACK_BLOCKED,
} as const;

export class AmazonIVSPlayerListener {
  constructor(
    private videoStartupService: VideoStartupService,
    private playbackService: PlaybackService,
    private errorService: ErrorService
  ) {}

  private onRebuffering() {
    logger.log('onRebuffering');
    this.playbackService.onRebuffering();
  }

  private onDurationChanged(payload: number) {
    logger.log('onDurationChanged: ' + payload);
  }

  private onError(payload: PlayerError) {
    logger.log('onError: ' + payload);
    this.errorService.onError(payload);
  }

  private onInitialized() {
    logger.log('onInitialized');
    if (!this.videoStartupService.finishedStartup) {
      this.videoStartupService.onInitialized();
    }
  }

  private onQualityChanged(payload: Quality) {
    logger.log('onQualityChanged: ' + payload);
    if (this.videoStartupService.finishedStartup) {
      this.playbackService.onQualityChange(payload);
    }
  }

  private onSeekCompleted(payload: number) {
    logger.log('onSeekCompleted: ' + payload);
  }

  private onTimeUpdate(payload: number) {
    logger.log('onTimeUpdate: ' + payload);
  }

  // HTMLVideoElement Events

  private onPlay(event: Event) {
    if (!this.videoStartupService.finishedStartup) {
      this.videoStartupService.onPlay();
    } else {
      logger.log('onPlay:', event);
    }
  }

  private onPause(event: Event) {
    if (this.videoStartupService.finishedStartup) {
      logger.log('onPause:', event);
      this.playbackService.onPause();
    }
  }

  private onPlaying(event: Event) {
    logger.log('onPlaying:', event);
    if (!this.videoStartupService.finishedStartup) {
      this.videoStartupService.onPlaying();
      this.playbackService.onStartupPlaying();
    } else {
      this.playbackService.onPlaying();
    }
  }

  startMonitoring(player: MediaPlayer) {
    this.startMonitoringIVSPlayer(player);
    this.startMonitoringHTMLVideoElement(player.getHTMLVideoElement());
  }

  private startMonitoringHTMLVideoElement(video: HTMLVideoElement) {
    video.addEventListener('play', this.onPlay.bind(this));
    video.addEventListener('playing', this.onPlaying.bind(this));
    video.addEventListener('pause', this.onPause.bind(this));
  }

  //https://aws.github.io/amazon-ivs-player-docs/1.18.0/web/interfaces/playereventtypemap.html
  private startMonitoringIVSPlayer(player: MediaPlayer) {
    player.addEventListener(IVS_PLAYER_EVENT.REBUFFERING, this.onRebuffering.bind(this));
    player.addEventListener(IVS_PLAYER_EVENT.DURATION_CHANGED, this.onDurationChanged.bind(this));
    player.addEventListener(IVS_PLAYER_EVENT.ERROR, this.onError.bind(this));
    player.addEventListener(IVS_PLAYER_EVENT.INITIALIZED, this.onInitialized.bind(this));
    player.addEventListener(IVS_PLAYER_EVENT.QUALITY_CHANGED, this.onQualityChanged.bind(this));
    player.addEventListener(IVS_PLAYER_EVENT.SEEK_COMPLETED, this.onSeekCompleted.bind(this));
    player.addEventListener(IVS_PLAYER_EVENT.TIME_UPDATE, this.onTimeUpdate.bind(this));

    // player.addEventListener(PlayerEventType.AUDIO_BLOCKED, this.onAudioBlocked);
    // player.addEventListener(PlayerEventType.BUFFER_UPDATE, this.onBufferUpdate);
    // player.addEventListener(PlayerEventType.MUTED_CHANGED, this.onMutedChanged);
    // player.addEventListener(PlayerEventType.NETWORK_UNAVAILABLE, this.onNetworkUnavailable);
    // player.addEventListener(PlayerEventType.PLAYBACK_BLOCKED, this.onPlaybackBlocked);
    // player.addEventListener(PlayerEventType.PLAYBACK_RATE_CHANGED, this.onPlaybackRateChanged);
    // player.addEventListener(PlayerEventType.TEXT_CUE, this.onTextCue);
    // player.addEventListener(PlayerEventType.TEXT_METADATA_CUE, this.onTextMetadataCue);
    // player.addEventListener(PlayerEventType.VOLUME_CHANGED, this.onVolumeChanged);
  }

  // for now we will keep those in here
  // once we have finished the implementation of IVS we can remove them

  // private onAudioBlocked() {}
  // private onBufferUpdate() {}
  // private onMutedChanged() {}
  // private onNetworkUnavailable() {}
  // private onPlaybackBlocked() {}
  // private onPlaybackRateChanged(payload: number) {}
  // private onTextCue(payload: TextCue) {}
  // private onTextMetadataCue(payload: TextMetadataCue) {}
  // private onVolumeChanged(payload: number) {}
}
